import React, { useState, useEffect, useRef } from 'react';
import styles from './Header.module.css';
import { Button } from '../../common/Button/Button';
import { Project, User } from '../../../types';
import { LocationIcon, ChevronDownIcon } from '../../common/Icons/Icons';
import { companiesAPI } from '../../../services/api';

// Logo imports - using public folder paths
const omgevingsCompasLogo = '/assets/logos/OmgevingsCompas_logo.svg';

interface HeaderProps {
  currentProject?: Project;
  projects?: Project[];
  user?: User;
  onProjectChange?: (project: Project) => void;
  onAllProjectsClick?: () => void;
  onLogout?: () => void;
  showProjectSelector?: boolean;
  showClientLogo?: boolean;
}

export const Header: React.FC<HeaderProps> = ({
  currentProject,
  projects = [],
  user,
  onProjectChange,
  onAllProjectsClick,
  onLogout,
  showProjectSelector = true,
  showClientLogo = true,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const projectDropdownRef = useRef<HTMLDivElement>(null);
  const userDropdownRef = useRef<HTMLDivElement>(null);

  // Get company logo from user company or current project
  useEffect(() => {
    const fetchCompanyLogo = async () => {
      // First try from current project
      if ((currentProject as any)?.company?.logo) {
        setCompanyLogo((currentProject as any).company.logo);
        return;
      }

      // Then try from user's company via API
      if (user && (user as any).company) {
        try {
          const companyId = typeof (user as any).company === 'object'
            ? (user as any).company._id || (user as any).company.id
            : (user as any).company;

          if (companyId) {
            const company = await companiesAPI.getById(companyId);
            if (company.logo) {
              setCompanyLogo(company.logo);
            }
          }
        } catch (error) {
          // Non-critical
        }
      }
    };

    fetchCompanyLogo();
  }, [user, currentProject]);

  const handleProjectClick = (project: Project) => {
    if (onProjectChange) {
      onProjectChange(project);
    }
    setDropdownOpen(false);
  };

  const handleAllProjectsClick = () => {
    if (onAllProjectsClick) {
      onAllProjectsClick();
    }
    setDropdownOpen(false);
  };

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
    setUserDropdownOpen(false);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (projectDropdownRef.current && !projectDropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setUserDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header className={styles.header}>
      <div className={styles.headerContent}>
        <a href="/" className={styles.logo}>
          <img src={omgevingsCompasLogo} alt="OmgevingsCompas" className={styles.logoImg} />
        </a>
        
        <nav className={styles.headerNav}>
          {showClientLogo && companyLogo && (
            <img
              src={companyLogo}
              alt="Company Logo"
              className={styles.clientLogo}
            />
          )}
          
          {showProjectSelector && currentProject && (
            <div className={styles.projectDropdown} ref={projectDropdownRef}>
              <button 
                className={styles.dropdownToggle}
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                <LocationIcon />
                <span>{currentProject.name}</span>
                <ChevronDownIcon />
              </button>
              
              {dropdownOpen && (
                <div className={styles.dropdownMenu}>
                  {projects.map(project => (
                    <div
                      key={project.id}
                      className={styles.dropdownItem}
                      onClick={() => handleProjectClick(project)}
                    >
                      <LocationIcon />
                      {project.name}
                    </div>
                  ))}
                  {projects.length > 1 && (
                    <>
                      <div className={styles.dropdownDivider} />
                      <div
                        className={`${styles.dropdownItem} ${styles.allProjects}`}
                        onClick={handleAllProjectsClick}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="3" width="7" height="7" />
                          <rect x="14" y="3" width="7" height="7" />
                          <rect x="14" y="14" width="7" height="7" />
                          <rect x="3" y="14" width="7" height="7" />
                        </svg>
                        Alle projecten
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          
          {user && (
            <div className={styles.userDropdown} ref={userDropdownRef}>
              <div
                className={styles.userProfile}
                onClick={() => setUserDropdownOpen(!userDropdownOpen)}
              >
                <div className={styles.userAvatar}>
                  {user.avatar ? (
                    <img src={user.avatar} alt="" className={styles.userAvatarImg} />
                  ) : (
                    user.initials || user.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'
                  )}
                </div>
                <span className={styles.userName}>
                  {user.name || 'Gebruiker'}
                </span>
                <ChevronDownIcon />
              </div>
              
              {userDropdownOpen && (
                <div className={styles.userDropdownMenu}>
                  <div
                    className={styles.dropdownItem}
                    onClick={() => {
                      window.location.href = '/profile';
                      setUserDropdownOpen(false);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    Profiel
                  </div>
                  <div
                    className={styles.dropdownItem}
                    onClick={() => {
                      window.location.href = '/settings';
                      setUserDropdownOpen(false);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="3" />
                      <path d="M12 1v6m0 6v6m11-11h-6m-6 0H1" />
                    </svg>
                    Instellingen
                  </div>
                  {(user?.role === 'admin' || user?.role === 'superAdmin') && (
                    <>
                      <div className={styles.dropdownDivider} />
                      <div
                        className={styles.dropdownItem}
                        onClick={() => { window.location.href = '/admin'; setUserDropdownOpen(false); }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                        Admin beheer
                      </div>
                    </>
                  )}
                  <div className={styles.dropdownDivider} />
                  <div
                    className={`${styles.dropdownItem} ${styles.logoutItem}`}
                    onClick={handleLogout}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    Uitloggen
                  </div>
                </div>
              )}
            </div>
          )}
          
          <Button variant="contact">Contact</Button>
        </nav>
      </div>
    </header>
  );
};